<template>
  <div
    class="mini-calendar__calendar"
    role="region"
    aria-label="Events Calendar"
    v-if="!isMonthView"
  >
    <VDatePicker
      :key="range"
      borderless
      trim-weeks
      ref="datepicker"
      v-model="range"
      @update:modelValue="dayclick"
      @update:pages="popover"
      :attributes="month_events"
    />
  </div>
</template>

<script>
import moment from "moment";
import { getFilteredEvents } from "./../api";
import { mapActions, mapGetters } from "vuex";
import { DATE_FORMAT } from "@/utils/config";
import { generateStaticDay } from "./../utils/util";

export default {
  name: "MiniCalendarCalendar",
  data() {
    return {
      range: moment().format("YYYY-MM-DD"),
      is_mounted: false,
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      lastPopoverCall: 0,
      isManualDateClick: false,
      lastSelectedDayClick: null,
    };
  },
  computed: {
    ...mapGetters(["date_range", "month_events", "navigationDirection"]),
  },
  mounted() {
    this.updateDatePicker();
  },
  watch: {
    date_range: function (newVal) {
      if (newVal.type != "custom_range") {
        const date = new Date();
        const year = date.getFullYear();
        const month = date.getMonth() + 1;

        this.month = month;
        this.year = year;
        this.$refs.datepicker.move({ month: month, year: year });

        if (
          newVal.start != moment(this.range).format(DATE_FORMAT) ||
          newVal.end != moment(this.range).format(DATE_FORMAT)
        ) {
          this.is_mounted = true;
          this.range = new Date();
        }
      }
    },
  },
  methods: {
    // upding the date object syncing with month navigation.
    updateDatePicker() {
      const selectedMonth = this.$store.state.selectedMonth || moment().format("YYYY-MM");
      const [year, month] = selectedMonth.split("-");
      if (this.$refs.datepicker) {
        const newDate = moment().year(parseInt(year)).month(parseInt(month)).date(1);
        this.range = newDate.format("YYYY-MM-DD");
        this.$nextTick(() => {
          this.$refs.datepicker.move({ month: parseInt(month), year: parseInt(year) });
          //this for removing unwanted highlighitng
          setTimeout(() => {
            if (!this.isManualDateClick) {
              // Only remove highlights if this was NOT from user clicking
              document.querySelectorAll(".vc-highlight-bg-solid, .vc-highlight-content-solid")
                .forEach(el => el.classList.remove("vc-highlight-bg-solid", "vc-highlight-content-solid"));
            }
            // Reset the flag for future usage
            this.isManualDateClick = false;
          }, 100);
        });
      }
    },
    filterEvents(start, end, isDayClick = false) {
      this.setLoading(true);
      if (isDayClick) {
        const formatted_start = start.format(DATE_FORMAT);
        const formatted_end = isDayClick ? start.format(DATE_FORMAT) : end.format(DATE_FORMAT);
        const searchTerm = this.$store.getters.search || "";

        const queryString = Object.entries(this.$store.state.selectedCategories || {})
          .flatMap(([key, terms]) => terms.map((term) => `${key}=${term}`))
          .join("&");

        const filtersQueryString = queryString || "";

        this.setSelectedMonth(start.format("YYYY-MM")); // Keep selected month updated even for day click

        getFilteredEvents(formatted_start, formatted_end, filtersQueryString, 1, searchTerm)
          .then((response) => {
            if (response) {
              const data = response.data ? response.data : [];
              this.setEventList({ eventlist: data, total: response.query.total });

              this.setDateRange({
                type: "custom_range",
                start: formatted_start,
                end: formatted_end,
              });
            }
            this.setLoading(false);
          })
          .catch(() => {
            this.setLoading(false);
          });
          return;
      }
      const selectedMonth = start.format("YYYY-MM");
      const formatted_start = moment(selectedMonth).startOf("month").format(DATE_FORMAT);
      const formatted_end = end.format(DATE_FORMAT);
      const searchTerm = this.$store.getters.search || "";
      const queryString = Object.entries(this.$store.state.selectedCategories || {})
      .flatMap(([key, terms]) => terms.map((term) => `${key}=${term}`))
      .join("&");
      const filtersQueryString = queryString || "";
      this.setSelectedMonth(selectedMonth);
      if (formatted_start != formatted_end) {
        let filtered = this.month_events.filter(
          (item) => item.id == formatted_start
        );
        if (!filtered.length) {
          getFilteredEvents(
            formatted_start,
            formatted_end,
            [],
            0,
            searchTerm
          ).then((response) => {
            if (response) {
              let data = response.data ? response.data : [];
              let dates = [];
              for (let event of data) {
                dates.push(moment(event.date.start.date).toDate());
              }

              this.setMonthEvents([
                ...this.month_events,
                generateStaticDay(dates, formatted_start),
              ]);
            }
          });
        }
      }
      getFilteredEvents(formatted_start, formatted_end, filtersQueryString, 1, searchTerm)
        .then((response) => {
          if (response) {
            const data = response.data ? response.data : [];
            this.setEventList({ eventlist: data, total: response.query.total });
            this.setDateRange({
              type: "custom_range",
              start: formatted_start,
              end: formatted_end,
            });
          }
          this.setLoading(false);
        })
        .catch(() => {
          this.setLoading(false);
        });
    },
    dayclick(e) {
      this.isManualDateClick = true;
      //this is for double click on same day
      if (e === null) {
        if (this.lastSelectedDayClick) {
          this.range = this.lastSelectedDayClick.clone().add(1, 'day').format("YYYY-MM-DD");
          //this is for double click on same day
          this.highlightSelectedDay(this.lastSelectedDayClick);
          this.filterEvents(this.lastSelectedDayClick, this.lastSelectedDayClick, true);
        }
        return;
      }
      if (this.is_mounted) {
        this.is_mounted = false;
        return;
      }
      const selectedDate = e ? moment(e) : moment();
      // Update the last selected day
      this.lastSelectedDayClick = selectedDate;
      //this is for double click on same day
      this.highlightSelectedDay(selectedDate);
      this.setDateManuallySelected(true);
      this.filterEvents(selectedDate, selectedDate, true);
    },
    highlightSelectedDay(selectedDate) {
      this.$nextTick(() => {
        setTimeout(() => {
          const allDays = document.querySelectorAll('.vc-day-content');
          allDays.forEach(day => {
            day.classList.remove('vc-highlight-content-solid', 'vc-blue');
          });
          const dayEl = document.querySelector(`.vc-day.id-${selectedDate.format("YYYY-MM-DD")} .vc-day-content`);
          if (dayEl) {
            setTimeout(() => {
              dayEl.classList.add('vc-highlight-content-solid', 'vc-blue');
            }, 100);
          } else {
            // If element is still missing (because DOM not ready yet), retry once after another slight delay
            setTimeout(() => {
              const retryDayEl = document.querySelector(`.vc-day.id-${selectedDate.format("YYYY-MM-DD")} .vc-day-content`);
              if (retryDayEl) {
                retryDayEl.classList.add('vc-highlight-content-solid', 'vc-blue');
              }
            }, 100);
          }
        }, 50);
      });
    },

    popover(e) {
      //this for removing unwanted highlighitng
      setTimeout(() => {
        if (!this.isManualDateClick) {
          // Only remove highlights if this was NOT from user clicking
          document.querySelectorAll(".vc-highlight-bg-solid, .vc-highlight-content-solid")
            .forEach(el => el.classList.remove("vc-highlight-bg-solid", "vc-highlight-content-solid"));
        }
        // Reset the flag for future usage
        this.isManualDateClick = false;
      }, 100);
      //Avoiding multiple calls
      const now = Date.now();
          if (now - this.lastPopoverCall < 200) {
        return;
      }
      this.lastPopoverCall = now;
      if (e.length > 0) {
        e = e[0];
      }
      if (e.year == this.year && e.month == this.month) {
        return;
      }
      //checking this month navigation
      if (this.navigationDirection == 'monthchange') {
        this.setDateManuallySelected(false);
        this.setNavigationDirection('');
      } else {
        this.setDateManuallySelected(true);
      }
      this.year = e.year;
      this.month = e.month;
      let start = moment(
        `${e.year}-${String(e.month).padStart(2, "0")}-01`,
        "YYYY-MM-DD"
      );
      const endofday = start.daysInMonth();
      const end = moment(
        `${e.year}-${String(e.month).padStart(2, "0")}-${String(
          endofday
        ).padStart(2, "0")}`,
        "YYYY-MM-DD"
      );
      const selectedMonth = start.format("YYYY-MM");
      this.setSelectedMonth(selectedMonth);
      this.setDateRange({
        type: "custom_range",
        start: start.format(DATE_FORMAT),
        end: end.format(DATE_FORMAT),
      });
      if (this.$refs.datepicker) {
          this.$refs.datepicker.move({ month: e.month, year: e.year });
      }
      this.$emit('month-changed', selectedMonth);
          this.filterEvents(start, end);
    },

    ...mapActions([
      "setDateRange",
      "setEventList",
      "setLoading",
      "setMonthEvents",
      "setSelectedMonth",
      "setDateManuallySelected",
      "setNavigationDirection",
    ]),
  },
};
</script>
